import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

//App
import agencyApi from "../../app/model/api/agencyApi";
import { queryKeys } from "./constants";
import { ContainerContentProps, SortBy } from "../../types";
import { AgencyContextData, AgencyState } from "../../types/agency";
import { EMPTY_GUID, GRID_PAGE_SIZE } from "../../utility/constants/constants";
import {
    selectIsActive,
    setIsActive,
    selectSearchValue,
    setSearchValue,
} from "../../store/employee";
import { SearchValue } from "../../store/types";
import { hasPermission, permissions } from "../../utility/helpers/claimHelper";
import Agency from "./Agency";



export const AgencyContext = React.createContext<AgencyContextData | null>(null);

const AgencyContainer: React.FC<ContainerContentProps> = (props) => {
    const dispatch = useDispatch();
    const [state, setState] = useState<AgencyState>({
        agencyId: null,
        activeToggle: useSelector(selectIsActive) ?? true,
        searchPayload: useSelector(selectSearchValue) ?? null,
        pageSize: GRID_PAGE_SIZE,
        currentPage: 1,
        sortBy: [{ field: "name", direction: 1 }],
    });

    const agencyGridQuery = useQuery({
        queryKey: [queryKeys.getAgencies],
        queryFn: () =>
            agencyApi.searchAgency({
                ...state.searchPayload,
                isActive: state.activeToggle,
                pageSize: state.pageSize,
                pageNumber: state.currentPage,
                sortBy: state.sortBy,
            }),
    });

    const agencyQuery = useQuery({
        queryKey: [queryKeys.getAgency, state.agencyId],
        queryFn: () => agencyApi.getAgency(state.agencyId ?? EMPTY_GUID),
        enabled: state.agencyId !== null,
        select: (data) => {

            return {
                ...data?.result,
            };
        },
    });

    const hasManageAgenciesPermission = hasPermission([
        permissions.MANAGE_AGENCIES,
    ]);

    const updateAgencyId = async (agencyId: string | null) => {
        setState((prevState) => ({
            ...prevState,
            agencyId: agencyId,
        }));
    };

    const updateActiveToggle = async () => {
        dispatch(setIsActive());
        setState((prevState) => ({
            ...prevState,
            activeToggle: !prevState.activeToggle,
        }));
    };

    const updateSortBy = async (sortBy: SortBy) => {
        const { field, direction } = sortBy;
        setState((prevState) => ({
            ...prevState,
            sortBy: [{ field, direction }],
        }));
    };

    const updateSearchPayload = async (values: SearchValue | null) => {
        if (values?.name) {
            dispatch(setSearchValue({ name: values.name }));
        }
        if (values && Object.keys(values).length) {
            setState((prevState) => ({
                ...prevState,
                searchPayload: values,
                currentPage: 1,
            }));
        } else {
            setState((prevState) => ({
                ...prevState,
                searchPayload: null,
                currentPage: 1,
                sortBy: [{ field: "name", direction: 1 }],
            }));
        }
    };

    const updateCurrentPage = async (currentPage: number) => {
        setState((prevState) => ({
            ...prevState,
            currentPage: currentPage,
        }));
    };

    const updateCurrentPageSize = async (pageSize: number) => {
        setState((prevState) => ({
            ...prevState,
            pageSize: pageSize,
        }));
    };

    const { mutate: deleteMutation } = useMutation<void, Error, string>({
        mutationFn: async (value: string) => {
            await agencyApi.deactivateAgency(value);
        },
    });

    const { mutate: activateMutation } = useMutation<void, Error, string>({
        mutationFn: async (value: string) => {
            await agencyApi.activateAgency(value);
        },
    });

    const contextValue: AgencyContextData = {
        data: agencyQuery?.data,
        id: state.agencyId,
        setId: updateAgencyId,
        activeToggle: state.activeToggle,
        setActiveToggleValue: updateActiveToggle,
        searchValue: state.searchPayload ?? undefined,
        setSearchValue: updateSearchPayload,
        paging: {
            currentPage: state.currentPage,
            totalSize: agencyGridQuery?.data?.totalSize ?? 0,
        },
        pageSize: state.pageSize,
        setCurrentPageSize: updateCurrentPageSize,
        setCurrentPage: updateCurrentPage,
        setSortBy: updateSortBy,
        refetchGrid: () => agencyGridQuery?.refetch(),
        hasManageAgenciesPermission: hasManageAgenciesPermission,
    };

    return (
        <AgencyContext.Provider value={contextValue}>
            <Agency
                data={agencyGridQuery?.data?.result}
                deleteMutation={deleteMutation}
                activateMutation={activateMutation}
                fullscreen={props.fullscreen}
            />
        </AgencyContext.Provider>
    );
};

export default AgencyContainer;
